import React, { useState, useEffect, useMemo } from "react";
import {
  FileSpreadsheet,
  Download,
  Calendar,
  Loader2,
  Building2,
  Users,
  MapPin,
} from "lucide-react";
import apiService from "../services/api";
import { useCompany } from "../context/CompanyContext";
import "../styles/GoldCleanReport.css";

const Report = () => {
  const { selectedCompany } = useCompany();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}`;
  });
  const [loadingTM, setLoadingTM] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Filter states
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const isKoral = selectedCompany?.id === 1;
  const isGoldClean = selectedCompany?.id === 2;

  // Fetch customers on mount
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const response = await apiService.getCustomers();
        setCustomers(response.customers || response || []);
      } catch (err) {
        console.error("Error fetching customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, [selectedCompany]);

  // Get unique customer names for filter
  const customerNames = useMemo(() => {
    const names = [...new Set(customers.map((c) => c.customer_name))].filter(
      Boolean,
    );
    return names.sort((a, b) => a.localeCompare(b, "he"));
  }, [customers]);

  // Get sites filtered by selected customer
  const filteredSites = useMemo(() => {
    let filtered = customers;
    if (selectedCustomer) {
      filtered = customers.filter((c) => c.customer_name === selectedCustomer);
    }
    const sites = [...new Set(filtered.map((c) => c.site_name))].filter(
      Boolean,
    );
    return sites.sort((a, b) => a.localeCompare(b, "he"));
  }, [customers, selectedCustomer]);

  // Reset site when customer changes
  useEffect(() => {
    setSelectedSite("");
  }, [selectedCustomer]);

  // Generate month options for the last 12 months
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}`;
      const label = date.toLocaleDateString("he-IL", {
        year: "numeric",
        month: "long",
      });
      options.push({ value, label });
    }
    return options;
  };

  const downloadBlob = (response, filename) => {
    const blob = new Blob([response], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleExportTM = async () => {
    if (!selectedMonth) {
      setError("יש לבחור חודש");
      return;
    }

    setLoadingTM(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiService.exportTMReport(
        selectedMonth,
        selectedCustomer,
        selectedSite,
      );
      const filename = `tm_report_${selectedMonth.replace("-", "_")}.xlsx`;
      downloadBlob(response, filename);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Export T&M error:", err);
      setError(err.message || "שגיאה בייצוא קובץ T&M");
    } finally {
      setLoadingTM(false);
    }
  };

  const handleExportCompanies = async () => {
    if (!selectedMonth) {
      setError("יש לבחור חודש");
      return;
    }

    setLoadingCompanies(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiService.exportCompaniesReport(selectedMonth);
      const filename = `companies_report_${selectedMonth.replace(
        "-",
        "_",
      )}.xlsx`;
      downloadBlob(response, filename);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Export Companies error:", err);
      setError(err.message || "שגיאה בייצוא קובץ חברות");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleExport = async () => {
    if (!selectedMonth) {
      setError("יש לבחור חודש");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      let response;
      let filename;

      if (isKoral) {
        response = await apiService.exportKoralReport(selectedMonth);
        filename = `koral_report_${selectedMonth.replace("-", "_")}.xlsx`;
      } else {
        response = await apiService.exportGoldCleanReport(selectedMonth);
        filename = `gold_clean_report_${selectedMonth.replace("-", "_")}.xlsx`;
      }

      downloadBlob(response, filename);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Export error:", err);
      setError(err.message || "שגיאה בייצוא הקובץ");
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (isKoral) return "דוח קורל";
    if (isGoldClean) return "דוח Gold Clean";
    return "דוח אקסל";
  };

  const getDescription = () => {
    if (isKoral) return "ייצוא דוח אקסל לפי לקוחות וקבלנים";
    if (isGoldClean) return "ייצוא דוח אקסל לפי אתרים וחודש";
    return "ייצוא דוח אקסל";
  };

  const getStructure = () => {
    if (isKoral) {
      return (
        <ul>
          <li>גיליון לכל לקוח</li>
          <li>גיליונות קבלנים (מוניר, איברהים, איתן שמיר, ואי גי אס)</li>
          <li>פירוט שעות, תעריפים ותשלומים</li>
        </ul>
      );
    }
    if (isGoldClean) {
      return (
        <ul>
          <li>גיליון לכל אתר</li>
          <li>פירוט עובדים, שעות ותשלומים</li>
        </ul>
      );
    }
    return null;
  };

  return (
    <div className='gold-clean-report-container'>
      <div className='report-header'>
        <div className='header-icon'>
          <FileSpreadsheet size={32} />
        </div>
        <div className='header-text'>
          <h1>{getTitle()}</h1>
          <p>{getDescription()}</p>
        </div>
      </div>

      <div className='report-card'>
        <div className='card-content'>
          <div className='form-section'>
            <label className='form-label'>
              <Calendar size={18} />
              בחר חודש
            </label>
            <select
              className='month-select'
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={loading}
            >
              {getMonthOptions().map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Customer and Site Filters */}
          {isKoral && (
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
                marginTop: "1rem",
              }}
            >
              <div
                className='form-section'
                style={{ flex: 1, minWidth: "200px" }}
              >
                <label className='form-label'>
                  <Building2 size={18} />
                  לקוח
                </label>
                <select
                  className='month-select'
                  value={selectedCustomer}
                  onChange={(e) => setSelectedCustomer(e.target.value)}
                  disabled={loadingCustomers}
                >
                  <option value=''>כל הלקוחות</option>
                  {customerNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className='form-section'
                style={{ flex: 1, minWidth: "200px" }}
              >
                <label className='form-label'>
                  <MapPin size={18} />
                  אתר
                </label>
                <select
                  className='month-select'
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                  disabled={loadingCustomers}
                >
                  <option value=''>כל האתרים</option>
                  {filteredSites.map((site) => (
                    <option key={site} value={site}>
                      {site}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className='info-section'>
            <h3>מבנה הקובץ:</h3>
            {getStructure()}
          </div>

          {error && <div className='error-message'>{error}</div>}

          {success && <div className='success-message'>הקובץ הורד בהצלחה!</div>}

          {/* New T&M and Companies Export Buttons */}
          {isKoral && (
            <div
              style={{
                display: "flex",
                gap: "1rem",
                flexWrap: "wrap",
                marginBottom: "1rem",
              }}
            >
              <button
                className='export-button'
                onClick={handleExportTM}
                disabled={loadingTM || loadingCompanies}
                style={{
                  flex: 1,
                  minWidth: "200px",
                  backgroundColor: "#10B981",
                }}
              >
                {loadingTM ? (
                  <>
                    <Loader2 size={20} className='spinning' />
                    מייצא T&M...
                  </>
                ) : (
                  <>
                    <Users size={20} />
                    ייצוא T&M
                  </>
                )}
              </button>
              <button
                className='export-button'
                onClick={handleExportCompanies}
                disabled={loadingTM || loadingCompanies}
                style={{
                  flex: 1,
                  minWidth: "200px",
                  backgroundColor: "#3B82F6",
                }}
              >
                {loadingCompanies ? (
                  <>
                    <Loader2 size={20} className='spinning' />
                    מייצא חברות...
                  </>
                ) : (
                  <>
                    <Building2 size={20} />
                    ייצוא חברות
                  </>
                )}
              </button>
            </div>
          )}

          {/* Info about new exports */}
          {isKoral && (
            <div
              className='info-section'
              style={{
                marginTop: "1rem",
                backgroundColor: "#f0f9ff",
                padding: "1rem",
                borderRadius: "8px",
              }}
            >
              <h4 style={{ marginBottom: "0.5rem" }}>פירוט הקבצים:</h4>
              <div style={{ display: "grid", gap: "0.5rem" }}>
                <div>
                  <strong style={{ color: "#10B981" }}>T&M:</strong> עובדים לפי
                  אתר, שעות, תעריפים, משכורת (לקבלנים), קבלן
                </div>
                <div>
                  <strong style={{ color: "#3B82F6" }}>חברות:</strong> עובדים
                  לפי לקוח (BH ואחרים), שעות, תעריפים, משכורת
                </div>
                <div
                  style={{
                    marginTop: "0.5rem",
                    fontSize: "0.85rem",
                    color: "#666",
                  }}
                >
                  <span
                    style={{
                      backgroundColor: "#FF6B6B",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      color: "white",
                      marginRight: "8px",
                    }}
                  >
                    אדום
                  </span>
                  עובד במספר אתרים
                  <span
                    style={{
                      backgroundColor: "#FFA500",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      color: "white",
                      marginRight: "8px",
                      marginLeft: "16px",
                    }}
                  >
                    כתום
                  </span>
                  חסר תעריף
                </div>
              </div>
            </div>
          )}

          <button
            className='export-button'
            onClick={handleExport}
            disabled={loading || loadingTM || loadingCompanies}
            style={{ marginTop: "1rem" }}
          >
            {loading ? (
              <>
                <Loader2 size={20} className='spinning' />
                מייצא...
              </>
            ) : (
              <>
                <Download size={20} />
                ייצוא דוח מלא
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Report;
